import { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation  } from "react-router-dom";
import { useCases } from "../../../hooks/useWorkshopQueries";
import {
  Badge, SLABadge, PriorityBadge, PageHeader, Spinner, SearchInput, EmptyState, ErrorState,
  STAGE_LABELS, STATUS_LABELS, stageBadge, statusBadge, formatDate, timeAgo,
} from "../../../Component/Workshop/UI/index";
import { FilterIcon } from "../../../Component/Workshop/UI/icons";

const STAGE_TABS = [
  { key: "ALL",               label: "All"           },
  { key: "CASE_CREATED",      label: "Case Created"  },
  { key: "SURVEY_PROCESS",    label: "Survey"        },
  { key: "WORKSHOP_REPAIR",   label: "Workshop"      },
  { key: "DELIVERY_PAYMENT",  label: "Delivery"      },
  { key: "CLAIM_SETTLEMENT",  label: "Settlement"    },
  { key: "SLA_BREACHED",      label: "SLA Breached"  },
];

export default function CasesPage() {
  const navigate    = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const globalSearch = searchParams.get("q") || "";
  const [activeTab, setActiveTab]   = useState("ALL");
  const [search, setSearch] = useState(globalSearch);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [statusFilter,setStatusFilter]= useState("");
  const [isTyping, setIsTyping] = useState(false);

  const [monthFilter, setMonthFilter] = useState(searchParams.get("month") || "");
  const [filterOpen, setFilterOpen]   = useState(false);
  const filterRef = useRef(null);

  const handleSearchChange = (value) => {
    setSearch(value);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value?.trim()) next.set("q", value);
        else next.delete("q");
        return next;
      },
      { replace: true }
    );
  };

  // ── Real query (uncomment when backend ready) ──────────────────────────────
  const filters = {};
  if (activeTab === "SLA_BREACHED") filters.sla = "BREACHED";
  else if (activeTab !== "ALL")     filters.stage = activeTab;
  if (debouncedSearch) filters.search = debouncedSearch;
  if (stageFilter)  filters.stage  = stageFilter;
  if (statusFilter) filters.status = statusFilter;

  if (monthFilter) filters.month = monthFilter;
  const { data, isLoading, isError, refetch } = useCases(filters);
  const cases = data?.cases || [];

  const location = useLocation();
  const highlightedCase = location.state?.highlightedCase;
  const highlightRowRef = useRef(null);

  const [activeHighlight, setActiveHighlight] = useState(highlightedCase);

  useEffect(() => {
  setIsTyping(true);
  const timer = setTimeout(() => {
    setDebouncedSearch(search);
    setIsTyping(false);
  }, 600); // 600ms delay

  return () => clearTimeout(timer);
}, [search]);

  useEffect(() => {
    if (globalSearch !== search) setSearch(globalSearch);
  }, [globalSearch]);

  useEffect(() => {
  if (!highlightedCase) return;
  const t = setTimeout(() => setActiveHighlight(null), 3000);
  return () => clearTimeout(t);
}, [highlightedCase]);

  useEffect(() => {
  if (highlightRowRef.current) {
    highlightRowRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
  }
}, [cases]);

useEffect(() => {
  if (!filterOpen) return;
  const handler = (e) => {
    if (filterRef.current && !filterRef.current.contains(e.target))
      setFilterOpen(false);
  };
  document.addEventListener("mousedown", handler);
  return () => document.removeEventListener("mousedown", handler);
}, [filterOpen]);

  return (
    <div className="wd-animate" style={{ display:"flex", flexDirection:"column", gap:16 }}>
      <PageHeader
        title="Cases"
        subtitle="Manage and track all accident cases"
        actions={
          <>
            <select className="wd-input wd-select" style={{ width:"auto" }}
              value={stageFilter} onChange={e => setStageFilter(e.target.value)}>
              <option value="">All Stages</option>
              {Object.entries(STAGE_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select className="wd-input wd-select" style={{ width:"auto" }}
              value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="">All Status</option>
              {Object.entries(STATUS_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <div style={{ position: "relative" }} ref={filterRef}>
              <button
                className={`wd-btn ${filterOpen || monthFilter ? "wd-btn-primary" : "wd-btn-ghost"}`}
                onClick={() => setFilterOpen(p => !p)}
              >
                <FilterIcon /> Filters {monthFilter && "·1"}
              </button>

              {filterOpen && (
                <div style={{
                  position: "absolute", right: 0, top: "calc(100% + 8px)", zIndex: 50,
                  background: "var(--wd-card)", border: "1px solid var(--wd-border)",
                  borderRadius: 10, padding: 16, minWidth: 220,
                  boxShadow: "0 8px 24px rgba(0,0,0,.12)"
                }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "var(--wd-t3)", marginBottom: 8 }}>
                    FILTER BY MONTH
                  </div>
                  <select
                    className="wd-input wd-select"
                    value={monthFilter}
                    onChange={e => {
                      const val = e.target.value;
                      setMonthFilter(val);
                      setSearchParams(prev => {
                        const next = new URLSearchParams(prev);
                        if (val) next.set("month", val);
                        else next.delete("month");
                        return next;
                      }, { replace: true });
                    }}
                    style={{ width: "100%" }}
                  >
                    <option value="">All Months</option>
                    {Array.from({ length: 12 }, (_, i) => {
                      const d = new Date(new Date().getFullYear(), new Date().getMonth() - i, 1);
                      const val = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
                      return (
                        <option key={val} value={val}>
                          {d.toLocaleString("default", { month: "long", year: "numeric" })}
                        </option>
                      );
                    })}
                  </select>
                  {monthFilter && (
                    <button
                      className="wd-btn wd-btn-ghost"
                      style={{ marginTop: 10, width: "100%", fontSize: 12 }}
                      onClick={() => {
                        setMonthFilter("");
                        setFilterOpen(false);
                        setSearchParams(prev => {
                          const next = new URLSearchParams(prev);
                          next.delete("month");
                          return next;
                        }, { replace: true });
                      }}
                    >
                      Clear
                    </button>
                  )}
                </div>
              )}
            </div>
          </>
        }
      />

      {/* Tab + Search row */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
        <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
          {STAGE_TABS.map(t => (
            <button key={t.key}
              className={`wd-filter-tab ${activeTab === t.key ? "active" : ""}`}
              onClick={() => setActiveTab(t.key)}
            >{t.label}</button>
          ))}
        </div>
        <SearchInput value={search} onChange={handleSearchChange} placeholder="Search case, name, vehicle, phone..." />
      </div>

      {/* Table */}
      <div className="wd-card">
        {isLoading ? <Spinner /> :
         isError   ? <ErrorState onRetry={() => {}} /> :
         cases.length === 0 ? <EmptyState message="No cases found" /> : (
          <div className="wd-table-wrap">
            <table className="wd-table">
              <thead>
                <tr>
                  <th>CASE</th><th>CUSTOMER</th><th>VEHICLE</th>
                  <th>PRIORITY</th><th>STAGE</th><th>STATUS</th><th>WORKSHOP</th>
                  <th>UPDATED</th><th>SLA</th>
                </tr>
              </thead>
              <tbody>
                {cases.map(c => (
                  <tr
                    key={c.id}
                    ref={c.accidentCaseNo === highlightedCase ? highlightRowRef : null}
                    onClick={() => navigate(`/admin/cases/${c.accidentCaseNo}`)}
                    style={{
                          transition: "background .6s, outline .6s",   // smooth fade-out
                          background: c.accidentCaseNo === activeHighlight ? "rgba(59,130,246,.13)" : undefined,
                          outline:    c.accidentCaseNo === activeHighlight ? "1px solid var(--wd-blue)" : undefined,
                        }}
                  >
                    <td>
                      <div style={{ fontFamily:"var(--wd-mono)", fontWeight:600, color:"var(--wd-blue)" }}>{c.accidentCaseNo}</div>
                      <div style={{ fontSize:11, color:"var(--wd-t3)", marginTop:2 }}>{formatDate(c.accidentDate)}</div>
                    </td>
                    <td>
                      <div style={{ fontWeight:500 }}>{c.customerName || <span style={{ color:"var(--wd-t3)", fontStyle:"italic" }}>—</span>}</div>
                      <div style={{ fontSize:11, color:"var(--wd-t3)", fontFamily:"var(--wd-mono)" }}>{c.customerPhone}</div>
                    </td>
                    <td>
                      <div style={{ fontFamily:"var(--wd-mono)", fontSize:12, fontWeight:500 }}>{c.vehicleNo}</div>
                      <div style={{ fontSize:11, color:"var(--wd-t3)" }}>{c.vehicleMake || "-"}</div>
                    </td>
                    <td><PriorityBadge priority={c.priority} /></td>
                    <td><Badge variant={stageBadge(c.stage)}>{STAGE_LABELS[c.stage] || c.stage}</Badge></td>
                    <td><Badge variant={statusBadge(c.currentStatus)}>{STATUS_LABELS[c.currentStatus] || c.currentStatus}</Badge></td>
                    <td style={{ color:"var(--wd-t2)", fontSize:13 }}>{c.workshopName || "—"}</td>
                    <td style={{ color:"var(--wd-t3)", fontSize:12 }}>{timeAgo(c.updatedAt)}</td>
                    <td><SLABadge status={c.slaStatus} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

